import { api } from "@/lib/api";

// Files at or above this size go through the chunked / resumable path.
export const CHUNK_UPLOAD_THRESHOLD = 100 * 1024 * 1024; // 100 MB
const CHUNK_LS_KEY = "iz_chunk_sessions_v1";

// ------------------ Adaptive chunk-size state (per-session) ------------------

function initAdaptive(hint = 5 * 1024 * 1024) {
  return { size: hint, min: 256 * 1024, max: 16 * 1024 * 1024, streak: 0 };
}
function onChunkOk(a) {
  a.streak += 1;
  // After a 3-chunk success streak, gently double up to the ceiling.
  if (a.streak >= 3 && a.size < a.max) {
    a.size = Math.min(a.max, a.size * 2);
    a.streak = 0;
  }
  return a;
}
function onChunkFail(a) {
  a.size = Math.max(a.min, Math.floor(a.size / 2));
  a.streak = 0;
  return a;
}

// ------------------ Resume-session tracking in localStorage ------------------

function readSessionMap() {
  try { return JSON.parse(localStorage.getItem(CHUNK_LS_KEY) || "{}"); }
  catch { return {}; }
}
function writeSessionMap(m) {
  try { localStorage.setItem(CHUNK_LS_KEY, JSON.stringify(m)); } catch {}
}
function fingerprintFor(file) {
  // name + size + lastModified is enough to spot a re-picked file across reloads
  return `${file.name}|${file.size}|${file.lastModified || 0}`;
}
function stashSession(fp, sid) {
  const m = readSessionMap();
  m[fp] = { sid, at: Date.now() };
  // Purge entries older than 24h
  const cutoff = Date.now() - 24 * 3600 * 1000;
  for (const k of Object.keys(m)) if ((m[k]?.at || 0) < cutoff) delete m[k];
  writeSessionMap(m);
}
function readStashed(fp) {
  const m = readSessionMap();
  const rec = m[fp];
  if (!rec) return null;
  if (Date.now() - rec.at > 24 * 3600 * 1000) return null;
  return rec.sid;
}
function clearStashed(fp) {
  const m = readSessionMap();
  delete m[fp];
  writeSessionMap(m);
}

// ------------------ Range utilities ------------------

function totalReceivedBytes(ranges) {
  return (ranges || []).reduce((s, [a, b]) => s + (b - a), 0);
}
function firstMissingOffset(ranges, totalSize) {
  const r = [...(ranges || [])].sort((x, y) => x[0] - y[0]);
  let cursor = 0;
  for (const [s, e] of r) {
    if (s > cursor) return cursor;
    if (e > cursor) cursor = e;
  }
  return cursor < totalSize ? cursor : totalSize;
}

// ------------------ Chunked uploader ------------------

/**
 * Upload a single file using the chunked / resumable endpoints.
 * Adaptive chunk sizing: starts at the server hint, halves on failure,
 * doubles after a 3-chunk success streak (up to the server max).
 * Resume: if the same file (name + size + mtime) was interrupted within
 * 24h, we pick up where we left off using the server's received_ranges.
 *
 * @param {File}   file        Native File object
 * @param {object} opts
 *   context         optional; "imos" bumps server-side cap to 300 MB
 *   onProgress(pct) called with 0..100 as bytes are ACK-ed
 *   signal          optional AbortSignal
 * @returns upload record ({id, size, original_filename, ...})
 */
export async function uploadChunked(file, { context, onProgress, signal } = {}) {
  const fp = fingerprintFor(file);
  const stashed = readStashed(fp);

  // 1. init (or resume)
  let init;
  try {
    const { data } = await api.post("/uploads/chunked/init", {
      filename: file.name,
      total_size: file.size,
      content_type: file.type || null,
      context: context || null,
      fingerprint: fp,
    });
    init = data;
  } catch (err) {
    if (stashed) clearStashed(fp);
    throw err;
  }
  stashSession(fp, init.session_id);

  // 2. If server had prior chunks (resume), rebase progress.
  let ranges = init.received_ranges || [];
  const emitProgress = () => {
    if (!onProgress) return;
    const pct = Math.min(100, Math.round((totalReceivedBytes(ranges) / file.size) * 100));
    onProgress(pct);
  };
  emitProgress();

  const adaptive = initAdaptive(init.chunk_size_hint || 5 * 1024 * 1024);
  adaptive.max = init.max_chunk_size || adaptive.max;
  adaptive.min = init.min_chunk_size || adaptive.min;

  // 3. Loop: pump byte-ranges until fully received.
  while (totalReceivedBytes(ranges) < file.size) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const offset = firstMissingOffset(ranges, file.size);
    const size = Math.min(adaptive.size, file.size - offset);
    const blob = file.slice(offset, offset + size);
    const form = new FormData();
    form.append("chunk", blob, `${offset}.bin`);

    let attempts = 0;
    let succeeded = false;
    let lastErr = null;
    while (attempts < 4 && !succeeded) {
      try {
        const { data } = await api.put(
          `/uploads/chunked/${init.session_id}/chunk?offset=${offset}`,
          form,
          {
            headers: { "Content-Type": "multipart/form-data" },
            signal,
            timeout: 300000,
          }
        );
        ranges = data.received_ranges || ranges;
        succeeded = true;
        onChunkOk(adaptive);
      } catch (err) {
        lastErr = err;
        attempts += 1;
        onChunkFail(adaptive);
        // Exponential backoff before retry
        await new Promise((r) => setTimeout(r, 500 * attempts));
        // If the chunk size we tried was already the min and we still failed,
        // stop retrying and bubble up.
        if (adaptive.size === adaptive.min && attempts >= 2) break;
      }
    }
    if (!succeeded) {
      throw lastErr || new Error("Chunk upload failed");
    }
    emitProgress();
  }

  // 4. Assemble on the server.
  const { data: rec } = await api.post(`/uploads/chunked/${init.session_id}/complete`);
  clearStashed(fp);
  onProgress?.(100);
  return rec;
}

/**
 * Upload one file to /api/uploads and return the file metadata.
 * @param context — optional. Pass "imos" for IMOS-module uploads to allow up to 300MB per file.
 *
 * Files at or above CHUNK_UPLOAD_THRESHOLD (100 MB) are automatically routed
 * through the resumable chunked upload path.
 */
export async function uploadOne(file, onProgress, context) {
  if (file && file.size >= CHUNK_UPLOAD_THRESHOLD) {
    return uploadChunked(file, { context, onProgress });
  }
  const form = new FormData();
  form.append("file", file);
  const url = context ? `/uploads?context=${encodeURIComponent(context)}` : "/uploads";
  const { data } = await api.post(url, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return data;
}

/**
 * Upload many files in parallel, returning list of file records in same order.
 * @param context — optional. Pass "imos" for IMOS-module uploads to allow up to 300MB per file.
 */
export async function uploadMany(files, onProgress, context) {
  const results = [];
  let done = 0;
  await Promise.all(
    files.map(async (f, idx) => {
      const rec = await uploadOne(f, undefined, context);
      results[idx] = rec;
      done += 1;
      onProgress?.(Math.round((done / files.length) * 100));
    })
  );
  return results;
}

/**
 * Convenience: IMOS-module uploads (bumps per-file cap from 60 MB → 300 MB on the backend).
 * Use for all uploads that go through the IMOS workflow: engineering drawings,
 * clarification attachments, review files, sign-off files, factory handoff files.
 */
export async function uploadManyImos(files, onProgress) {
  return uploadMany(files, onProgress, "imos");
}
export async function uploadOneImos(file, onProgress) {
  return uploadOne(file, onProgress, "imos");
}

/**
 * Fetch a file's bytes as a Blob URL (Authorization is applied via api).
 * Consumers MUST URL.revokeObjectURL(url) on unmount.
 */
export async function fetchAsBlobUrl(fileId) {
  const res = await api.get(`/uploads/${fileId}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

export function kindLabel(k) {
  return {
    quotation_pdf: "Quotation PDF",
    twod: "2D Drawings",
    threed: "3D Renders",
    ppt: "PPT / Deck",
  }[k] || k;
}

export function inferKindFromFile(file) {
  const name = file.name?.toLowerCase() || "";
  const type = file.type?.toLowerCase() || "";
  if (type === "application/pdf" || name.endsWith(".pdf")) return "quotation_pdf";
  if (type.startsWith("image/")) return "twod";
  if (name.endsWith(".ppt") || name.endsWith(".pptx") || type.includes("presentation")) return "ppt";
  return "quotation_pdf";
}

/**
 * Build a direct browser-loadable URL for /api/uploads/{id}.
 * Appends ?token=<jwt> so <img src> / <a href> can auth without an Authorization header.
 */
export function fileUrl(fileId) {
  if (!fileId) return "";
  const token = localStorage.getItem("iz_token") || "";
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fileId}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

/**
 * Same auth pattern for any GET-style /api path that will be opened directly by the browser
 * (e.g. CSV downloads). Preserves an existing query string.
 */
export function authedApiUrl(apiPath) {
  const token = localStorage.getItem("iz_token") || "";
  const base = `${process.env.REACT_APP_BACKEND_URL}${apiPath.startsWith("/") ? "" : "/"}${apiPath}`;
  if (!token) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}token=${encodeURIComponent(token)}`;
}
